import type {LucideIcon} from 'lucide-react'
import type * as React from 'react'
import {Tooltip, TooltipContent, TooltipTrigger} from '@/components/ui/tooltip'
import {cn} from '@/lib/utils'

/**
 * Semantic tones — these mean something (good / bad / needs attention).
 * Distinct from `CategorySlot`, which only needs to be told apart.
 */
export type BadgeTone =
	| 'success'
	| 'warning'
	| 'destructive'
	| 'info'
	| 'brand'
	| 'neutral'

/** A step in the 12-hue categorical ramp. See `--category-*` in globals.css. */
export type CategorySlot = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

export type TintBadgeSize = 'xs' | 'sm' | 'md'

type TintBadgeBase = {
	children: React.ReactNode
	size?: TintBadgeSize
	/** Leading dot at full strength — the fill and label stay tinted. */
	dot?: boolean
	/** Pulses the dot. Only meaningful alongside `dot`. */
	pulse?: boolean
	/** Leading icon, rendered before the dot. */
	icon?: LucideIcon
	/** Hairline in the same hue. For pills that sit on `bg-card`. */
	bordered?: boolean
	/** When set, wraps the badge in a Tooltip and marks it `cursor-default`. */
	tooltip?: React.ReactNode
	className?: string
}

/**
 * Exactly one color source, enforced by the `?: never` arms.
 *
 * Without them `<TintBadge tone='success' category={3} />` would compile and
 * silently pick whichever the implementation checked first. With them, passing
 * two is a type error — and so is passing none, since each member declares its
 * own prop as required.
 *
 * The escape hatch is a spread: `<TintBadge {...props} />` where `props` is a
 * wider object bypasses the check entirely. Direct JSX is protected.
 */
type TintBadgeSource =
	| {tone: BadgeTone; category?: never; color?: never}
	| {category: CategorySlot; tone?: never; color?: never}
	| {color: string | null; tone?: never; category?: never}

export type TintBadgeProps = TintBadgeBase & TintBadgeSource

const SIZE: Record<TintBadgeSize, string> = {
	xs: 'gap-1 px-1.5 py-px text-[10px]',
	sm: 'gap-1.5 px-2 py-0.5 text-[11px]',
	md: 'gap-1.5 px-2.5 py-0.5 text-xs',
}

const DOT_SIZE: Record<TintBadgeSize, string> = {
	xs: 'size-1',
	sm: 'size-1.5',
	md: 'size-1.5',
}

const ICON_SIZE: Record<TintBadgeSize, string> = {
	xs: 'size-2.5 shrink-0',
	sm: 'size-3 shrink-0',
	md: 'size-3 shrink-0',
}

const TONE_FILL: Record<BadgeTone, string> = {
	success: 'bg-success/15 text-success',
	warning: 'bg-warning/15 text-warning',
	destructive: 'bg-destructive/15 text-destructive',
	info: 'bg-info/15 text-info',
	brand: 'bg-brand/15 text-brand',
	// Neutral is the one tone with no hue to tint — the muted surface *is* the
	// recipe, so it takes the flat token pair rather than a /15 wash.
	neutral: 'bg-muted text-muted-foreground',
}

const TONE_BORDER: Record<BadgeTone, string> = {
	success: 'border-success/40',
	warning: 'border-warning/40',
	destructive: 'border-destructive/40',
	info: 'border-info/40',
	brand: 'border-brand/40',
	neutral: 'border-border',
}

const TONE_DOT: Record<BadgeTone, string> = {
	success: 'bg-success',
	warning: 'bg-warning',
	destructive: 'bg-destructive',
	info: 'bg-info',
	brand: 'bg-brand',
	neutral: 'bg-muted-foreground',
}

/**
 * The categorical ramp's fill + label pair.
 *
 * The label is deliberately NOT `text-category-N`: the full-strength hue
 * measures ~2.8:1 as text on the cream canvas, below the 4.5:1 AA floor.
 * `--category-N-foreground` is a second, darker ramp at the same hue angles.
 *
 * ⚠️ These strings, plus CATEGORY_BORDER and CATEGORY_DOT below, are the ONLY
 * place `bg-category-*` / `text-category-*-foreground` appear as literals in
 * the repo. Tailwind v4 scans source text, so an interpolated form like
 * `` `bg-category-${slot}` `` emits no CSS at all — and the failure is silent:
 * the map still typechecks, the badge still renders, the fill is just gone.
 * Guarded by tint-badge.test.ts and tests/browser/tint-badge.browser.test.tsx.
 */
const CATEGORY_FILL: Record<CategorySlot, string> = {
	1: 'bg-category-1/15 text-category-1-foreground',
	2: 'bg-category-2/15 text-category-2-foreground',
	3: 'bg-category-3/15 text-category-3-foreground',
	4: 'bg-category-4/15 text-category-4-foreground',
	5: 'bg-category-5/15 text-category-5-foreground',
	6: 'bg-category-6/15 text-category-6-foreground',
	7: 'bg-category-7/15 text-category-7-foreground',
	8: 'bg-category-8/15 text-category-8-foreground',
	9: 'bg-category-9/15 text-category-9-foreground',
	10: 'bg-category-10/15 text-category-10-foreground',
	11: 'bg-category-11/15 text-category-11-foreground',
	12: 'bg-category-12/15 text-category-12-foreground',
}

const CATEGORY_BORDER: Record<CategorySlot, string> = {
	1: 'border-category-1/40',
	2: 'border-category-2/40',
	3: 'border-category-3/40',
	4: 'border-category-4/40',
	5: 'border-category-5/40',
	6: 'border-category-6/40',
	7: 'border-category-7/40',
	8: 'border-category-8/40',
	9: 'border-category-9/40',
	10: 'border-category-10/40',
	11: 'border-category-11/40',
	12: 'border-category-12/40',
}

const CATEGORY_DOT: Record<CategorySlot, string> = {
	1: 'bg-category-1',
	2: 'bg-category-2',
	3: 'bg-category-3',
	4: 'bg-category-4',
	5: 'bg-category-5',
	6: 'bg-category-6',
	7: 'bg-category-7',
	8: 'bg-category-8',
	9: 'bg-category-9',
	10: 'bg-category-10',
	11: 'bg-category-11',
	12: 'bg-category-12',
}

/** Neutral used when a slot is unknown. */
const CATEGORY_DOT_FALLBACK = 'bg-muted-foreground'

/**
 * The runtime-tint contract: carries a user-chosen color to the stylesheet.
 *
 * Pair with `data-tint` on the same element. Exported so surfaces that need
 * the derivation but not the badge's geometry — the close modal's outcome
 * segments, which are interactive buttons — get identical colors without
 * `TintBadge` having to grow an `onClick`.
 */
export function tint(color: string): React.CSSProperties {
	return {'--tint': color} as React.CSSProperties
}

/**
 * Slot → dot class, for the bare-dot surfaces that aren't badges (RegionDot in
 * the filter dropdowns). Keeps every `bg-category-*` literal in this one file.
 */
export function categoryDotClass(slot: CategorySlot | null): string {
	return slot === null ? CATEGORY_DOT_FALLBACK : CATEGORY_DOT[slot]
}

/**
 * A pill with a tinted fill and a matching, contrast-corrected label.
 *
 * Replaces roughly a dozen hand-rolled restatements of
 * `bg-X/15 text-X rounded-full px-2 py-0.5 text-xs font-medium`, and three
 * separate formulas for tinting a runtime hex.
 *
 * Deliberately non-interactive — a `<span>`, no `onClick`, no `asChild`.
 * Interactive things that want this look (segmented outcome buttons) share the
 * color math via `tint()` rather than this component's geometry.
 *
 * ⚠️ `className` merges last through twMerge, so a caller passing `bg-card` or
 * `text-muted-foreground` silently deletes the tint. Same footgun class as the
 * `text-sm`-on-Input trap documented in CLAUDE.md.
 */
export function TintBadge({
	children,
	tone,
	category,
	color,
	size = 'md',
	dot = false,
	pulse = false,
	icon: Icon,
	bordered = false,
	tooltip,
	className,
}: TintBadgeProps) {
	// A runtime color can never be a Tailwind class, so it travels as an inline
	// custom property and globals.css owns the derivation (dark mode is a
	// `.dark` ancestor, unreachable from an inline style). An explicit null
	// means "colorless" and degrades to the neutral recipe.
	const runtime = color ?? null

	const fill = runtime
		? undefined
		: category !== undefined
			? CATEGORY_FILL[category]
			: TONE_FILL[tone ?? 'neutral']

	const edge = runtime
		? undefined
		: category !== undefined
			? CATEGORY_BORDER[category]
			: TONE_BORDER[tone ?? 'neutral']

	const dotFill = runtime
		? undefined
		: category !== undefined
			? CATEGORY_DOT[category]
			: TONE_DOT[tone ?? 'neutral']

	const badge = (
		<span
			data-tint={runtime ? '' : undefined}
			data-tint-bordered={runtime && bordered ? '' : undefined}
			style={runtime ? tint(runtime) : undefined}
			className={cn(
				'inline-flex w-fit max-w-full shrink-0 items-center rounded-full font-medium whitespace-nowrap',
				SIZE[size],
				bordered && 'border',
				fill,
				bordered ? edge : undefined,
				tooltip && 'cursor-default',
				className,
			)}
		>
			{Icon ? <Icon className={ICON_SIZE[size]} /> : null}
			{dot ? (
				<span
					data-tint-dot={runtime ? '' : undefined}
					className={cn(
						'shrink-0 rounded-full',
						DOT_SIZE[size],
						dotFill,
						pulse && 'animate-pulse',
					)}
				/>
			) : null}
			{/* truncate needs a non-flex block: applying it to the flex container
			    above would do nothing, and would clip the icon and dot too. */}
			<span className='min-w-0 truncate'>{children}</span>
		</span>
	)

	if (!tooltip) return badge

	return (
		<Tooltip>
			<TooltipTrigger asChild>{badge}</TooltipTrigger>
			<TooltipContent className='max-w-xs'>{tooltip}</TooltipContent>
		</Tooltip>
	)
}
